/**
 * Admin chrome: the section nav, small shared layout atoms, and the banner that
 * appears when a screen is reading fixtures because its endpoint is not live.
 */
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { AlertTriangle, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";
import { AdminStyles } from "./AdminStyles";
import { isUsingFixtures, subscribeToFixtureMode } from "../data/transport";

/* ------------------------------------------------------------------ */
/* Navigation                                                          */
/* ------------------------------------------------------------------ */

const SECTIONS = [
  { to: "/admin", label: "Dashboard", end: true },
  // First after the dashboard on purpose: during a sitting this is the screen
  // an invigilator lives on, and hunting for it is not something to do while a
  // student's clock is running.
  { to: "/admin/live", label: "Live", end: false },
  { to: "/admin/exams", label: "Exams", end: false },
  { to: "/admin/questions", label: "Questions", end: false },
  // Sits beside Questions because it is about question QUALITY: the review
  // queue asks whether a question reads correctly, this asks whether it
  // behaved correctly.
  { to: "/admin/analysis", label: "Analysis", end: false },
  { to: "/admin/attempts", label: "Attempts", end: false },
  { to: "/admin/students", label: "Students", end: false },
];

export function AdminNav() {
  return (
    <nav className="flex items-center gap-1" aria-label="Admin sections">
      {SECTIONS.map((section) => (
        <NavLink
          key={section.to}
          to={section.to}
          end={section.end}
          className={({ isActive }) =>
            cn(
              "rounded-md px-2 py-1 text-xs transition-colors",
              isActive
                ? "bg-accent-soft text-accent"
                : "text-ink-muted hover:bg-surface-sunken hover:text-ink",
            )
          }
        >
          {section.label}
        </NavLink>
      ))}
    </nav>
  );
}

/* ------------------------------------------------------------------ */
/* Fixture banner                                                      */
/* ------------------------------------------------------------------ */

/**
 * The Worker's admin handlers are landing progressively. When a call falls back
 * to fixtures this says so, in one line, so nothing on screen is mistaken for
 * the real attempt.
 */
export function FixtureBanner() {
  const [on, setOn] = useState(isUsingFixtures());
  useEffect(() => subscribeToFixtureMode(setOn), []);
  if (!on) return null;

  return (
    <div
      role="status"
      className="flex items-center gap-2 rounded-md border border-review/30 bg-review-soft px-3 py-2"
    >
      <AlertTriangle className="size-3.5 shrink-0 text-review" />
      <p className="text-xs text-ink">
        Showing sample data — at least one API endpoint on this screen is not
        live yet.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Layout atoms                                                        */
/* ------------------------------------------------------------------ */

export function PageTitle({
  title,
  meta,
  actions,
}: {
  title: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <h1 className="truncate text-xl font-semibold tracking-tight text-ink">
          {title}
        </h1>
        {meta ? <div className="mt-1 text-xs text-ink-muted">{meta}</div> : null}
      </div>
      {actions ? (
        <div className="flex shrink-0 items-center gap-2">{actions}</div>
      ) : null}
    </div>
  );
}

/** A labelled number. Used in the attempt header and the student summary. */
export function Stat({
  label,
  value,
  hint,
  tone = "default",
  icon: Icon,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: "default" | "answered" | "review" | "violation";
  icon?: LucideIcon;
}) {
  const toneClass =
    tone === "answered"
      ? "text-answered"
      : tone === "review"
        ? "text-review"
        : tone === "violation"
          ? "text-violation"
          : "text-ink";

  return (
    <div className="min-w-0 px-4 py-3">
      <div className="flex items-center gap-1.5">
        {Icon ? <Icon className="size-3.5 text-ink-faint" /> : null}
        <p className="truncate text-2xs font-medium text-ink-muted">{label}</p>
      </div>
      <p className={cn("tabular mt-1 text-lg font-semibold", toneClass)}>{value}</p>
      {hint ? <p className="mt-0.5 truncate text-2xs text-ink-faint">{hint}</p> : null}
    </div>
  );
}

/** One sentence, left aligned. No illustrations (docs/DESIGN.md §10). */
export function EmptyState({ children }: { children: ReactNode }) {
  return <p className="px-4 py-6 text-xs text-ink-faint">{children}</p>;
}

export function Loading({ label = "Loading…" }: { label?: string }) {
  return <p className="px-4 py-6 text-xs text-ink-faint">{label}</p>;
}

export function ErrorNote({ error }: { error: unknown }) {
  const message =
    error instanceof Error ? error.message : "Something went wrong.";
  return (
    <div className="flex items-start gap-2 rounded-md border border-violation/30 bg-violation-soft px-3 py-2">
      <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-violation" />
      <p className="text-xs text-ink">{message}</p>
    </div>
  );
}

export { AdminStyles };
